import React, { useState } from "react";
import { useDispatch } from "react-redux";
import axios from "axios";
import {
  Button,
  TextField,
  Typography,
  Container,
  Grid,
  CssBaseline,
  Box,
  IconButton,
  InputAdornment,
} 
from "@mui/material";
import { Navigate, useNavigate } from "react-router-dom";
import { styled } from "@mui/system";
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import signinImage from "../../assets/ForgotPwd.png";
import logoImage from "../../assets/logo.png";
import { showAppAlert } from "../../store/reducers/appreducer";
import {baseURL} from "../../config";

const Root = styled("div")(({ theme }) => ({
  display: "flex",
  minHeight: "100vh",
  overflow: "hidden", 
}));

const LeftSide = styled("div")(({ theme }) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  padding: theme.spacing(4),
  backgroundColor: "#FFFFFF",
  maxWidth: "50%",
}));

const RightSide = styled("div")(({ theme }) => ({
  flex: 1,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  backgroundColor: "#F3F7FF",
}));

const ImageContainer = styled("div")(({ theme }) => ({
  width: "514px",
  height: "467px",
  backgroundImage: `url(${signinImage})`,
  backgroundRepeat: "no-repeat",
  backgroundSize: "cover",
  backgroundPosition: "center",
  padding: "45.78px 17.89px 45.78px 26.27px",
}));

const FormContainer = styled(Container)(({ theme }) => ({
  maxWidth: "400px",
  margin: "0 auto",
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  marginBottom: theme.spacing(2),
}));

const Logo = styled("img")(({ theme }) => ({
  width: "51.12px",
  height: "50.64px",
  opacity: "1",
  marginBottom: "12px",
}));

const LogoText = styled(Typography)(({ theme }) => ({
  marginLeft: "5px",
  marginTop: "20px",
  fontFamily: "Raleway",
  fontSize: "32px",
  fontWeight: 600,
  lineHeight: "39.01px",
  textAlign: "left",
  color: "#1E186B",
  marginBottom: "3px",
}));

const LostText = styled(Typography)(({ theme }) => ({
  width: "350px",
  height: "38px",
  top: "212px",
  left: "123px",
  fontFamily: "Raleway",
  fontSize: "32px",
  fontWeight: 600,
  lineHeight: "37.57px",
  textAlign: "left",
  color: "#5088FF",
  marginBottom: "12px",
}));

const RecoverText = styled(Typography)(({ theme }) => ({
  width: "400px",
  height: "33px",
  top: "258px",
  left: "123px",
  fontFamily: "Raleway",
  fontSize: "28px",
  fontWeight: 600,
  lineHeight: "32.87px",
  textAlign: "left",
  color: "#313D4F",
  marginBottom: "6px",
}));

const DetailsText = styled(Typography)(({ theme }) => ({
  width: "272px",
  height: "19px",
  top: "302px",
  left: "123px",
  fontFamily: "Raleway",
  fontSize: "16px",
  fontWeight: 500,
  lineHeight: "18.78px",
  textAlign: "left",
  color: "#67768D",
  marginBottom: "48px",
}));

const CustomButton = styled(Button)(({ theme }) => ({
  width: "100%",
  height: "56px",
  padding: "8px 8px 8px 8px",
  gap: "8px",
  borderRadius: "8px 8px 8px 8px",
  backgroundColor: "#5088FF",
  color: "#FFFFFF",
  marginTop: theme.spacing(8),
}));

const FieldLabel = styled(Typography)(({ theme }) => ({
  fontFamily: "Raleway",
  fontSize: "14px",
  fontWeight: 500,
  lineHeight: "22.4px",
  textAlign: "left",
  color: "#262626",
  marginBottom: theme.spacing(0.3),
}));



const ForgotPwd = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [data, setData] = useState({
    email: "",
    password: "",
    first_name: "",
    last_name: "",
    phone: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isValid = validateForm();

    if (isValid) {
      setLoading(true);
      try {
        const response = await axios.post(`${baseURL}forgotPassword/`, {
          email: data.email,
        });

        dispatch(
          showAppAlert({
            show:true,
            alertType:"success", 
            alertMessage:"Email to Reset Password has been sent!!"
          })
        )
        navigate("/signin");
      } catch (error) {
        setLoading(false);
        const message = error.response?.data?.message || "Something went wrong.";
        dispatch(
          showAppAlert({
            show:true,
            alertType:"error", 
            alertMessage:message, 
          })
        )
      } finally {
        setLoading(false);
      }
    }
  };

  
  const validateForm = () => {
    let tempErrors = {};
    tempErrors.email = !data.email;
    setErrors(tempErrors);
    return Object.values(tempErrors).every((error) => !error);
  };


    const handleSignIn = () => {
    navigate("/signin") ; 
    };

  return (
    <Root>
      <CssBaseline />
      <LeftSide>
        <FormContainer>
          <LogoContainer>
            <Logo src={logoImage} alt="Logo" />
            <LogoText>HR-AI</LogoText>
          </LogoContainer>
          <LostText>Lost your Password?</LostText>
          <RecoverText>Enter your email to recover</RecoverText>
          <DetailsText>Enter your details to proceed further</DetailsText>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FieldLabel>Email Address*</FieldLabel>
                <TextField
                  variant="outlined"
                  required
                  fullWidth
                  id="email"
                  placeholder="Enter Email"
                  name="email"
                  value={data.email}
                  onChange={handleChange}
                  error={errors.email}
                  helperText={errors.email && "Email is required"}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          edge="end"
                        >
                          <EmailOutlinedIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
            <CustomButton
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
            >
              {loading ? "Verifying your email..." : "Recover"}
            </CustomButton>
            <CustomButton
              fullWidth
              variant="contained"
              onClick={handleSignIn} // Handle click event to go back
              style={{ marginTop: "1rem" }}
            >
              Go Back to Sign In
            </CustomButton>
          </form>
        </FormContainer>
      </LeftSide>
      <RightSide>
        <ImageContainer />
      </RightSide>
    </Root>
  );
};

export default ForgotPwd;
